import React, { useEffect, useState } from "react";
import { Box, Heading, useToast, Button, HStack, Text } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { BsArrowLeft, BsLightning } from "react-icons/bs";
import RequestForm from "../components/RequestForm";
import VendorDetailsSection from "../components/VendorDetailsSection";
import procurementApi from "../services/procurementApi";
import organizationalStructureApi from "../services/organizationalStructureApi";

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const rand = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const futureDate = (daysAhead) => {
  const d = new Date();
  d.setDate(d.getDate() + daysAhead);
  return d.toISOString().split("T")[0];
};

const TEST_TITLES = [
  "Annual AutoCAD License Renewal",
  "Office Furniture Upgrade Q2 2026",
  "Laptop Procurement for New Hires",
  "Server Room UPS Replacement",
  "Marketing Brochure Printing",
  "Cloud Hosting Subscription Renewal",
  "Office Pantry Supplies Restock",
  "Employee ID Card Printer Purchase",
  "Firewall Appliance Procurement",
  "Conference Room AV Equipment",
];

const TEST_DESCRIPTIONS = [
  "Procurement of licensed software and related services for the department as per approved budget allocation.",
  "Purchase of ergonomic office furniture including adjustable desks and chairs to improve workplace standards.",
  "Acquisition of high-performance laptops with standard configuration for onboarding new team members.",
  "Replacement of ageing UPS units in the server room to ensure uptime during power fluctuations.",
  "Printing of marketing collateral including brochures, banners and standees for the upcoming campaign.",
  "Renewal of annual cloud infrastructure subscription to support production workloads.",
  "Monthly replenishment of pantry essentials including tea, coffee, water and snacks for the office.",
  "Purchase of a PVC card printer for in-house employee ID badge generation.",
  "Procurement of next-gen firewall appliance for perimeter network security hardening.",
  "Upgrade of conference room audio-visual setup including projector, speakers and video bar.",
];

const TEST_LINE_ITEMS = [
  {
    itemName: "AutoCAD LT License",
    unit: "license",
    priceRange: [15000, 45000],
  },
  {
    itemName: "Ergonomic Office Chair",
    unit: "pcs",
    priceRange: [8000, 22000],
  },
  {
    itemName: "Dell Latitude 5540 Laptop",
    unit: "pcs",
    priceRange: [65000, 95000],
  },
  { itemName: "APC Smart-UPS 3000VA", unit: "pcs", priceRange: [18000, 35000] },
  { itemName: "A4 Brochure (200 gsm)", unit: "pcs", priceRange: [5, 25] },
  {
    itemName: "AWS Reserved Instance (1yr)",
    unit: "subscription",
    priceRange: [120000, 350000],
  },
  { itemName: "Toner Cartridge HP 26A", unit: "pcs", priceRange: [2500, 4500] },
  { itemName: "Logitech Rally Bar", unit: "pcs", priceRange: [85000, 130000] },
  {
    itemName: "Cisco Meraki MX67 Firewall",
    unit: "pcs",
    priceRange: [45000, 90000],
  },
  {
    itemName: "Standing Desk Converter",
    unit: "pcs",
    priceRange: [6000, 15000],
  },
];

const PRIORITIES = ["LOW", "MEDIUM", "HIGH", "URGENT"];

function generateTestData(orgStructure) {
  const units = (orgStructure.units || []).map((i) => i.code).filter(Boolean);
  const departments = (orgStructure.departments || [])
    .map((i) => i.code)
    .filter(Boolean);
  const branches = (orgStructure.branches || [])
    .map((i) => i.code)
    .filter(Boolean);
  const categories = (orgStructure.categories || [])
    .map((i) => i.name)
    .filter(Boolean);

  const idx = rand(0, TEST_TITLES.length - 1);
  const itemCount = rand(1, 3);
  const lineItems = [];
  const usedIndices = new Set();

  for (let i = 0; i < itemCount; i++) {
    let li;
    do {
      li = rand(0, TEST_LINE_ITEMS.length - 1);
    } while (usedIndices.has(li));
    usedIndices.add(li);

    const template = TEST_LINE_ITEMS[li];
    const qty = rand(1, 10);
    const unitPrice = rand(template.priceRange[0], template.priceRange[1]);
    lineItems.push({
      id: Date.now() + i,
      itemName: template.itemName,
      description: `${template.itemName} — standard specification`,
      quantity: qty,
      unitPrice,
      unit: template.unit,
      totalPrice: qty * unitPrice,
    });
  }

  const budget = lineItems.reduce((s, li) => s + li.totalPrice, 0);

  return {
    title: TEST_TITLES[idx],
    description: TEST_DESCRIPTIONS[idx],
    unit: units.length ? pick(units) : "",
    department: departments.length ? pick(departments) : "",
    branch: branches.length ? pick(branches) : "",
    category: categories.length ? pick(categories) : "",
    priority: pick(PRIORITIES),
    estimatedBudget: budget,
    justification: `Budget approved under department allocation. Required for ${pick(["operational continuity", "team expansion", "compliance requirements", "infrastructure upgrade", "productivity improvement"])}.`,
    expectedDeliveryDate: futureDate(rand(7, 60)),
    lineItems,
  };
}

const RequestCreate = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [prefillData, setPrefillData] = useState(null);
  const [orgStructure, setOrgStructure] = useState({
    units: [],
    departments: [],
    branches: [],
    categories: [],
  });

  useEffect(() => {
    fetchOrgStructure();
  }, []);

  const fetchOrgStructure = async () => {
    try {
      const [unitsResult, departmentsResult, branchesResult, categoriesResult] =
        await Promise.allSettled([
          organizationalStructureApi.getUnits({ isActive: true }),
          organizationalStructureApi.getDepartments({ isActive: true }),
          organizationalStructureApi.getBranches({ isActive: true }),
          organizationalStructureApi.getCategories({ isActive: true }),
        ]);

      const units = unitsResult.status === "fulfilled" ? unitsResult.value : [];
      const departments =
        departmentsResult.status === "fulfilled" ? departmentsResult.value : [];
      const branches =
        branchesResult.status === "fulfilled" ? branchesResult.value : [];
      const categories =
        categoriesResult.status === "fulfilled" ? categoriesResult.value : [];

      setOrgStructure({
        units: units || [],
        departments: departments || [],
        branches: branches || [],
        categories: categories || [],
      });
    } catch (error) {
      console.error("Failed to load org structure for Quick Fill:", error);
    }
  };

  const handleSubmit = async (formData) => {
    try {
      setLoading(true);
      const createResponse = await procurementApi.createRequest({
        ...formData,
        requestUrlBase: window.location.origin,
      });

      const createdRequest = createResponse?.data || createResponse;
      const createdRequestId = createdRequest?._id || createdRequest?.id;

      toast({
        title: "Request created successfully",
        description: "Your procurement request has been saved as draft",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      if (createdRequestId) {
        navigate(
          `/procurement/requests/${createdRequestId}?openVendorQuotation=1`,
        );
      } else {
        navigate("/procurement/requests");
      }
    } catch (error) {
      toast({
        title: "Error creating request",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/procurement/requests");
  };

  return (
    <Box p={6}>
      <HStack mb={6} justifyContent="space-between">
        <HStack>
          <Button
            leftIcon={<BsArrowLeft />}
            variant="ghost"
            onClick={handleCancel}
          >
            Back
          </Button>
          <Heading size="lg">New Procurement Request</Heading>
        </HStack>
        <Button
          leftIcon={<BsLightning />}
          colorScheme="yellow"
          variant="solid"
          size="sm"
          onClick={() => setPrefillData(generateTestData(orgStructure))}
        >
          Quick Fill
        </Button>
      </HStack>
      <Box bg="white" p={6} borderRadius="lg" borderWidth="1px">
        <RequestForm
          initialData={prefillData}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={loading}
        />
        <Box mt={10}>
          <Text fontSize="sm" color="gray.600" mb={3}>
            Need a new supplier from a site location? Add/register the vendor
            below and include the site location in vendor details.
          </Text>
          <VendorDetailsSection
            requestId={null} // No requestId yet, so VendorPOProposalList will render add form only
            requestStatus={"DRAFT"}
            approvalHistory={[]}
            onStatusChange={() => {}}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default RequestCreate;
